import { forwardRef } from "react";

import { DateTime } from "luxon";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useTranslate } from "@/common/hooks/useTranslate";
import { BaseFormDate, BaseFormDateProps } from "@/forms-design-system/FormDate/BaseFormDate";
import { formatTimeWithTimezone, getDateInTimezone, shouldDisplayYear } from "@/forms-design-system/FormDate/helper";

export type LabeledFormDateProps = Omit<BaseFormDateProps, "subtitle"> & {
  now?: Date;
  timezone?: string | null;
};

export const LabeledFormDate = forwardRef<HTMLDivElement, LabeledFormDateProps>(
  ({ date, now, timezone, ...rest }, ref) => {
    const { isoLocale } = useLocaleContext();
    const luxonNow = now ? getDateInTimezone(now, timezone) : DateTime.now().setZone(timezone || undefined);
    const luxonDate = getDateInTimezone(date, timezone);
    const { t } = useTranslate();

    const timeDisplay = formatTimeWithTimezone(luxonDate, isoLocale);

    return (
      <BaseFormDate
        date={date}
        subtitle={t("common", "dateRangeStartDate", {
          date: luxonDate.toLocaleString(
            {
              year: shouldDisplayYear(luxonDate, luxonNow) ? "numeric" : undefined,
              month: "short",
              day: "numeric",
            },
            { locale: isoLocale }
          ),
          time: timeDisplay,
        })}
        {...rest}
        ref={ref}
      />
    );
  }
);

LabeledFormDate.displayName = "LabeledFormDate";
